<template>
	<div
		:id="`dropdown-${_uid}`"
		v-click-outside="onClickOutside"
		data-cy="dropdown"
	>
		<div
			v-if="label && showLabel"
			data-cy="dropdown-label"
			class="dropdown__heading"
		>
			<label
				class="dropdown__label"
				:for="`dropdown-${_uid}`"
			>
				{{ label }}

				<helio-information
					v-if="infoMessage"
					:message="infoMessage"
					:gap="infoMessageGap"
					:color="infoColor"
					:icon="infoIcon"
					:icon-weight="infoIconWeight"
					:skin="infoMessageSkin"
				/>
			</label>
		</div>

		<div
			:class="[
				{
					'is-opened': opened,
					'is-disabled': disabled,
					'is-filled': !showPlaceholder,
					'is-search-results': searchedTerm,
				},
				`dropdown--${size}`
			]"
			:aria-label="ariaLabel"
			data-cy="dropdown-parent-wrapper"
			class="dropdown__wrapper"
		>
			<Trap :disabled="!showTrap">
				<div
					class="dropdown"
					data-cy="dropdown-wrapper"
					:tabindex="disabled ? '-1' : '0'"
					:aria-label="label"
					@click="toggle"
					@keyup.space="toggle"
					@keyup.enter="toggle"
				>
					<span
						v-if="showPlaceholder"
						class="dropdown__placeholder"
					>
						<helio-icon
							v-if="iconToken"
							:token="iconToken"
							:weight="iconWeight"
							class="dropdown__placeholder-icon"
						/>

						{{ placeholder }}
					</span>

					<span
						v-if="showNormalValue"
						class="dropdown__value"
						data-cy="dropdown-value"
					>
						<helio-icon
							v-if="iconToken"
							:token="iconToken"
							:weight="iconWeight"
							class="dropdown__placeholder-icon"
						/>

						{{ selected[0].label }}

						<template v-if="selected.length > 1">
							&nbsp;(+{{ selected.length - 1 }})
						</template>
					</span>

					<span
						v-if="showAvatarValue"
						class="dropdown__value dropdown__value--avatar"
					>
						<helio-avatar
							size="xx-small"
							:user="{
								image: selected[0].image
							}"
						/>

						{{ selected[0].label }}
					</span>

					<span
						v-if="searchActive"
						class="dropdown__search"
						data-cy="dropdown-search"
					>
						<helio-field
							:placeholder="$t('dropdown.search')"
							:autofocus="searchFocus"
							:show-validators="false"
							icon="search"
							icon-weight="light"
							icon-position="left"
							@changed="search"
						/>
					</span>

					<helio-icon
						class="dropdown__caret"
						token="angle-down"
						weight="regular"
					/>
				</div>

				<div
					ref="list-wrapper"
					class="dropdown__list-wrapper"
					aria-label="dropdown selector"
					role="dialog"
				>
					<ul
						v-show="isDropdownListShown"
						ref="list"
						:class="{
							'scroll': isScrollable,
							'dropdown__list--with-reset': reset,
						}"
						class="dropdown__list"
						data-cy="dropdown-list"
						:tabindex="disabled ? '-1' : tabIndex"
						:aria-hidden="!opened ? 'true' : null"
						:aria-label="$t('aria-label.dropdown')"
					>
						<li
							v-for="(option, index) in currentOptions"
							:ref="option.disabled ? 'inactive-item' : 'item'"
							:key="index"
							:class="{
								'is-selected' : isSelected(option) && !multiple && !option.disabled,
								'is-disabled': option.disabled,
								'dropdown__item--icon' : layout === 'icon',
								'dropdown__item--avatar' : layout === 'avatar',
								'dropdown__item--multiple' : multiple,
							}"
							class="dropdown__item"
							data-cy="dropdown-item"
							:data-label="option.label"
							:tabindex="option.disabled ? -1 : tabIndex"
							:aria-selected="isSelected(option)"
							:aria-label="`${option.label}: ${isSelected(option) ?
								$t('aria-label.selected') :
								$t('aria-label.not-selected')}`"
							@click="option.disabled ? '' : select(option)"
							@keyup.enter="option.disabled ? '' : select(option)"
							@keyup.space="option.disabled ? '' : select(option)"
						>
							<template v-if="layout === 'normal' && !multiple">
								<helio-icon
									v-if="itemIconToken"
									:token="itemIconToken"
									:weight="itemIconWeight"
									class="dropdown__icon"
									:class="`dropdown__icon--${itemIconPosition}`"
									@keydown.enter.stop="$emit('icon-click', option)"
									@keydown.space.stop="$emit('icon-click', option)"
									@click.stop="$emit('icon-click', option)"
								/>

								<span
									class="lowlight"
									v-html="highlightTerms(option.label, searchedTerm)"
								/>
							</template>

							<template v-if="multiple">
								<helio-checkbox
									:initial-checked="isSelected(option)"
									:label="option.label"
									tabindex="-1"
								/>
							</template>

							<template v-if="layout === 'avatar'">
								<helio-avatar
									:image="option.image"
									:user="{
										image: option.image
									}"
									size="xx-small"
								/>

								<span
									class="lowlight"
									v-html="highlightTerms(option.label, searchedTerm)"
								/>
							</template>
						</li>

						<li
							v-if="currentOptions.length === 0"
							class="dropdown__item dropdown__item--empty"
						>
							{{ $t('dropdown.no-results') }}
						</li>
					</ul>

					<div
						v-if="reset"
						class="dropdown__reset"
						data-cy="dropdown-reset"
						@keyup.enter="clear"
						@keyup.space="clear"
						@click="clear"
					>
						<helio-icon
							token="redo"
							weight="regular"
						/>

						{{ resetText ? resetText : $t('dropdown.reset') }}
					</div>
				</div>
			</Trap>
		</div>

		<div
			v-if="hint"
			class="dropdown__hint"
		>
			{{ hint }}
		</div>

		<div
			v-if="showTags"
			class="dropdown__tags"
		>
			<ul
				class="tags__list"
				data-cy="tags-list"
			>
				<li
					v-for="(item, index) in selected"
					:key="index"
					:class="{
						'tag__item--delete': !disabled,
					}"
					class="tag__item"
					data-cy="tags-item"
				>
					{{ item.label }}

					<helio-icon
						v-if="!disabled"
						class="tags__delete"
						token="times"
						weight="light"
						data-cy="tags-item-delete"
						@keydown.enter.stop="remove(item)"
						@keydown.space.stop="remove(item)"
						@click.stop="remove(item)"
					/>
				</li>
			</ul>
		</div>

		<div
			v-if="errors.length > 0"
			class="dropdown__error"
		>
			<helio-errors :items="errors" />
		</div>
	</div>
</template>

<script>
import Trap from 'vue-focus-lock';
import Avatar from '../avatar/Avatar.vue';
import Checkbox from '../checkbox/Checkbox.vue';
import Errors from '../errors/Errors.vue';
import Field from '../field/Field.vue';
import Icon from '../icon/Icon.vue';
import Information from '../information/Information.vue';

export default {
	name: 'Dropdown',

	components: {
		Trap,
		'helio-avatar': Avatar,
		'helio-checkbox': Checkbox,
		'helio-errors': Errors,
		'helio-field': Field,
		'helio-icon': Icon,
		'helio-information': Information,
	},

	props: {
		closeOnSelect: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the Dropdown closes when a selection is made',
		},

		disabled: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the Dropdown will be disabled for the user',
		},

		hint: {
			type: String,
			required: false,
			default: null,
			description: 'Sets a hint that will appear below the unopened Dropdown',
		},

		iconToken: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the icon that will appear to the left of the selected item when the Dropdown is in its closed state',
		},

		iconWeight: {
			type: String,
			required: false,
			default: 'regular',
			validator: value => [
				'bold',
				'brand',
				'light',
				'regular',
			].indexOf(value) !== -1,
			description: 'Sets the weight of the iconToken',
		},

		infoColor: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the color of the infoIcon. The colour provided must be one of those in the $colors-list.',
		},

		infoIcon: {
			type: String,
			required: false,
			default: 'question-circle',
			description: 'Sets the icon that will appear next to the Dropdown\'s label. Clicking the icon will open a tooltip.',
		},

		infoIconWeight: {
			type: String,
			required: false,
			default: 'bold',
			description: 'Sets the weight of the icon that will appear next to the Dropdown\'s label',
		},

		infoMessage: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the message that will appear in the infoIcon\'s tooltip',
		},

		infoMessageGap: {
			type: Number,
			required: false,
			default: 12,
			description: 'Sets the space in px between the anchor and the point of the Tooltip',
		},

		infoMessageSkin: {
			type: String,
			required: false,
			default: 'light',
			validator: value => [
				'dark',
				'light',
			].indexOf(value) !== -1,
			description: 'Sets whether the Tooltip will have a dark or light skin',
		},

		initialErrors: {
			type: Array,
			required: false,
			default: () => [],
			description: 'Sets the text that will appear below the Dropdown',
		},

		initialFocus: {
			type: String,
			required: false,
			default: '',
			description: 'Sets which item will be focused on by default',
		},

		initialSelected: {
			type: [Array, Object],
			required: false,
			default: null,
			description: 'Sets which items will already be selected by default',
		},

		itemIconPosition: {
			type: String,
			required: false,
			default: 'right',
			validator: value => [
				'left',
				'right',
			].indexOf(value) !== -1,
			description: 'Sets the horizontal alignment of the itemIcon',
		},

		itemIconToken: {
			type: String,
			required: false,
			default: null,
			description: 'Sets whether there will be an icon in the list item and which icon it will be',
		},

		itemIconWeight: {
			type: String,
			required: false,
			default: 'regular',
			validator: value => [
				'bold',
				'brand',
				'light',
				'regular',
			].indexOf(value) !== -1,
			description: 'Sets the weight of the itemIcon',
		},

		label: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the text of the Dropdown\'s label',
		},

		layout: {
			type: String,
			required: false,
			default: 'normal',
			validator: value => [
				'avatar',
				'icon',
				'normal',
			].indexOf(value) !== -1,
			description: 'Sets the layout of the list item (e.g., "avatar")',
		},

		multiple: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether multiple items can be selected',
		},

		options: {
			type: Array,
			required: true,
			description: 'Supplies information about each list item to the Dropdown component',
		},

		placeholder: {
			type: String,
			required: false,
			default: '',
			description: 'Sets the placeholder text that will appear in the input field',
		},

		reset: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether there will be a reset button in the Dropdown',
		},

		resetText: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the text that will appear in the reset button',
		},

		searchable: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the user can enter text into an input field to search list items',
		},

		showLabel: {
			type: Boolean,
			required: false,
			default: true,
			description: 'Set whether we show the label or not',
		},

		showTags: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether tags will be shown below the closed Dropdown component',
		},

		size: {
			type: String,
			required: false,
			default: 'normal',
			validator: value => [
				'normal',
				'large',
			].indexOf(value) !== -1,
			description: 'Sets the height of the Dropdown component',
		},
	},

	data() {
		return {
			closeable: !this.searchActive,
			currentOptions: this.options,
			errors: [],
			indexOfCurrentSelection: -1,
			isDropdownListShown: false,
			isScrollable: false,
			lastFocusedElement: null,
			opened: false,
			searchActive: false,
			searchedTerm: '',
			selected: [],
			showTrap: false,
			tabIndex: '-1',
		};
	},

	computed: {
		ariaLabel() {
			return this.label && !this.showLabel ? this.label : null;
		},

		showPlaceholder() {
			return (this.selected.length === 0 && !this.searchActive) || this.showTags;
		},

		showNormalValue() {
			return this.selected.length > 0 && !this.searchActive && this.layout !== 'avatar' && !this.showTags;
		},

		showAvatarValue() {
			return this.selected.length > 0 && this.layout === 'avatar' && !this.searchActive;
		},
	},

	watch: {
		selected(newSelection) {
			this.$emit('changed', [...newSelection]);
		},

		initialErrors(newErrors) {
			this.errors = newErrors;
		},

		initialSelected() {
			this.selected = [];

			this.assignInitialSelectedValues();
		},

		options(newOptions) {
			this.currentOptions = newOptions;

			if (!!newOptions && this.selected.length === 1) {
				const selected = this.selected[0];
				if (
					selected !== undefined
					&& selected.value !== undefined
					&& !newOptions.find(option => option.label === selected.label)
				) {
					this.select(newOptions.find(option => option.value === selected.value));
				}
			}
		},

		opened(newVal) {
			if (this.closingListWrapperTimeout) {
				clearTimeout(this.closingListWrapperTimeout);
			}

			if (newVal) {
				// shown instantly during open animation
				this.isDropdownListShown = true;
			} else {
				// wait for close animation to finish before hiding
				// NOTE: the delay must match the CSS transition time
				this.closingListWrapperTimeout = setTimeout(() => {
					this.isDropdownListShown = false;
				}, 200);
			}
		},
	},

	mounted() {
		window.addEventListener('keyup', this.checkForEscape);
		window.addEventListener('keyup', this.checkForArrows);
		this.assignInitialSelectedValues();

		if (this.initialErrors) {
			this.errors = this.initialErrors;
		}

		window.addEventListener('resize', this.onResize);
		setTimeout(() => {
			this.onResize();
		}, 1000);
	},

	beforeDestroy() {
		window.removeEventListener('keyup', this.checkForEscape);
		window.removeEventListener('keyup', this.checkForArrows);
		window.removeEventListener('resize', this.onResize);
	},

	methods: {
		// * Opening and closing methods
		toggle(e) {
			if (this.disabled) {
				return;
			}

			let tag = null;

			if (typeof e !== 'undefined') {
				tag = e.target.tagName;
			}

			if (this.opened && tag !== 'INPUT') {
				this.close();
			} else {
				this.open();

				if (this.searchable) {
					this.searchActive = true;
					this.searchFocus = true;
				}
			}
		},

		open() {
			this.opened = true;
			this.tabIndex = '0';
			this.lastFocusedElement = document.activeElement;
			this.showTrap = true;
			this.$emit('open');

			if (!!this.initialFocus && this.$refs.list) {
				const selector = `.dropdown__item[data-label="${this.initialFocus}"]`;
				const dropdownList = this.$refs.list;
				const dropdownItem = dropdownList.querySelector(selector);

				if (dropdownItem) {
					const { offsetTop, clientHeight } = dropdownItem;
					dropdownList.scrollTo(0, (offsetTop - clientHeight));
				}
			}
		},

		close() {
			if (this.searchable) {
				this.searchActive = false;
				this.searchFocus = false;
				this.currentOptions = this.options;
			}

			this.showTrap = false;
			this.tabIndex = '-1';
			setTimeout(() => {
				if (this.lastFocusedElement) {
					this.lastFocusedElement.focus();
				}
			}, 100);
			this.opened = false;

			this.$emit('close');
		},

		onClickOutside() {
			if (this.opened) {
				this.close();
				this.searchedTerm = '';
			}
		},

		// * Selection methods

		assignInitialSelectedValues() {
			if (this.initialSelected && this.initialSelected.length > 0) {
				this.selected = [...this.initialSelected];
			} else if (this.initialSelected && !(this.initialSelected instanceof Array)) {
				this.selected.push(this.initialSelected);
			}
		},

		select(option) {
			if (
				this.multiple
				&& this.selected.indexOf(option) < 0
			) {
				this.selected.push(option);
			} else if (
				this.multiple
				&& this.selected.indexOf(option) >= 0
			) {
				this.remove(option);
			} else {
				this.selected.splice(0, 1, option);
			}

			if (this.closeOnSelect) {
				this.searchActive = false;
				this.toggle();
			}
		},

		isSelected(option) {
			const selected = this.selected.find(selection => selection.label === option.label) != null;

			const sectionTitle = option.role === 'presentation';
			const inactiveOption = option.disabled;

			return (sectionTitle || inactiveOption) ? false : selected;
		},

		remove(option) {
			const index = this.selected.indexOf(option);
			this.selected.splice(index, 1);
		},

		clear() {
			this.selected = [];
			this.close();
		},

		// * Other methods
		onResize() {
			if (this.$refs.list) {
				const { scrollHeight } = this.$refs.list;
				const threshold = this.reset ? 163 : 200;

				this.isScrollable = (scrollHeight > threshold);
			}
		},

		search(term) {
			this.$emit('search', term);
			this.searchedTerm = term;

			this.currentOptions = this.options
				.filter(option => option.label.toLowerCase().match(term.toLowerCase()));
		},

		highlightTerms(dropdownOption, searchTerm) {
			if (!searchTerm) {
				return dropdownOption;
			}

			return dropdownOption.replace(new RegExp(searchTerm, 'gi'), (match) => `<span class="highlight">${match}</span>`);
		},

		checkForEscape(event) {
			const { keyCode } = event;

			if (keyCode === 27) {
				this.onClickOutside();
			}
		},

		checkForArrows(event) {
			const { keyCode } = event;

			// Up arrow
			if (keyCode === 38 && this.opened) {
				event.preventDefault();
				const listArray = [...this.$refs.item];
				if (this.indexOfCurrentSelection > 0) {
					this.indexOfCurrentSelection -= 1;
				}
				listArray[this.indexOfCurrentSelection].focus();
			}
			// Down arrow
			if (keyCode === 40 && this.opened) {
				event.preventDefault();
				const listArray = [...this.$refs.item];
				if (this.indexOfCurrentSelection < listArray.length - 1) {
					this.indexOfCurrentSelection += 1;
				}
				listArray[this.indexOfCurrentSelection].focus();
			}
		},
	},
};
</script>

<style lang="scss" src="./dropdown.scss" />
